import React, { useState, useEffect } from "react";
import {
  useNavigate,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import Sidebar from "./components/Sidebar";
import DashboardStats from "./components/Dashboard/DashboardStats";
import ProfileHeader from "./components/Dashboard/ProfileHeader";
import BatchTableView from "./components/Dashboard/BatchTableView";
import AllInvoicesPage from "./components/Dashboard/AllInvoices";
import InstructorManagement from "./components/Dashboard/InstructorManagement";
import LoadingScreen from "./components/common/LoadingScreen";
import WhatsAppButton from "./components/common/WhatsApp";
import AuthPage from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import { TrainerAPI } from "./api/TrainerAPI";
import ProfileEditModal from "./components/Dashboard/ProfileEditModal";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ReferAndEarn from "./components/Dashboard/ReferAndEarn";

const TrainerPortalApp = () => {
  const { user, loading, onboardingCompleted } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Start closed on mobile
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [trainerProfile, setTrainerProfile] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // isAdmin is derived later, after the auth guard
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(true); // Keep open on desktop
      } else {
        setIsSidebarOpen(false); // Close on mobile
      }
    };

    handleResize(); // Set initial state
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchTrainerProfile = async () => {
      if (!user?.email) return;

      try {
        const data = await TrainerAPI.getTrainerProfile();
        setTrainerProfile(data);
      } catch (err) {
        console.error("Failed to load trainer profile", err);
      }
    };

    fetchTrainerProfile();
  }, [user]);

  useEffect(() => {
    const fetchBatches = async () => {
      if (user?.email) {
        try {
          // const batchList = await TrainerAPI.getBatches();
          // setBatches(batchList);
        } catch (err) {
          console.error("Error loading batches:", err);
        } finally {
          setLoadingBatches(false);
        }
      }
    };
    fetchBatches();
  }, [user]);

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname.replace("/", "") || "dashboard";
    switch (path) {
      case "dashboard":
        return isAdmin ? "Admin Dashboard" : "Dashboard";
      case "batches":
        return "All Batches";
      case "all-invoices":
        return "All Invoices";
      case "instructors":
        return "Instructor Management";
      default:
        return path.charAt(0).toUpperCase() + path.slice(1);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!user) return <AuthPage />;
  
  // Gate: first-time trainers must complete onboarding (admins skip)
  const isAdmin = user?.role === "admin";
  if (!isAdmin && !onboardingCompleted) return <OnboardingPage />;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-[#FC7A69]/5 via-white to-[#221E45]/5">
      {/* Sidebar */}
      <Sidebar
        activeModule={location.pathname}
        onModuleChange={navigate}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        openProfileModal={() => setIsProfileModalOpen(true)}
        isSidebarOpen={isSidebarOpen}
      />

      {/* Main Content Area */}
      <div
        className={`
        flex-1 flex flex-col min-h-screen transition-all duration-300
        ${isSidebarOpen && window.innerWidth >= 768 ? "ml-0" : "ml-0"}
      `}
      >
        {/* Top Navigation Bar - Mobile Optimized */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm">
          <div className="flex items-center justify-between p-4">
            {/* Mobile: Empty space for menu button (handled by Sidebar) */}
            <div className="w-10 md:hidden"></div>

            {/* Desktop: Page Title */}
            <div className="hidden md:block">
              <h1 className="text-lg font-semibold text-gray-800">
                {getPageTitle()}
              </h1>
              {isAdmin && (
                <p className="text-sm text-gray-500">Administrator Access</p>
              )}
            </div>

            {/* Profile Header - Always Right Aligned */}
            <ProfileHeader
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              profile={trainerProfile}
            />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="min-h-full">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/dashboard"
                element={
                  <div className="animate-fadeIn">
                    <DashboardStats showTable={false} />
                  </div>
                }
              />
              <Route
                path="/batches"
                element={
                  <div className="animate-fadeIn p-4 sm:p-6">
                    <BatchTableView
                      batches={batches}
                      onBack={() => navigate("/dashboard")}
                      onBatchClick={() => {}}
                      loading={loadingBatches}
                    />
                  </div>
                }
              />
              <Route
                path="/all-invoices"
                element={
                  <div className="animate-fadeIn p-4 sm:p-6">
                    <AllInvoicesPage />
                  </div>
                }
              />


             <Route
  path="/refer-and-earn"
  element={
    <div className="animate-fadeIn p-4 sm:p-6">
      <ReferAndEarn />
    </div>
  }
/>


              {/* Admin Only Route - Instructor Management */}
              {isAdmin && (
                <Route
                  path="/instructors"
                  element={
                    <div className="animate-fadeIn">
                      <InstructorManagement />
                    </div>
                  }
                />
              )}

              <Route
                path="*"
                element={
                  <div className="flex items-center justify-center min-h-screen p-4">
                    <div className="text-center">
                      <div className="text-6xl mb-4">🔍</div>
                      <h1 className="text-2xl font-bold text-gray-800 mb-2">
                        Page Not Found
                      </h1>
                      <p className="text-gray-600 mb-6">
                        The page you're looking for doesn't exist.
                      </p>
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="px-6 py-3 bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white rounded-lg hover:shadow-lg transition-all duration-200"
                      >
                        Back to Dashboard
                      </button>
                    </div>
                  </div>
                }
              />
            </Routes>
          </div>
        </main>

        {/* WhatsApp Button - Mobile Optimized */}
        <div className="fixed bottom-4 right-4 z-40">
          <WhatsAppButton />
        </div>
      </div>

      {/* Profile Edit Modal */}
      <ProfileEditModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        profile={trainerProfile}
        onSave={async () => {
          const updated = await TrainerAPI.getTrainerProfile();
          setTrainerProfile(updated);
          setIsProfileModalOpen(false);
        }}
      />
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
      {/* Custom Styles for Animations */}
      <style>{`
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Custom scrollbar for webkit browsers */
        ::-webkit-scrollbar {
          width: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #FC7A69;
          border-radius: 3px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #e66955;
        }
      `}</style>
    </div>
  );
};

export default TrainerPortalApp;